import { supabase, Profile } from '../lib/supabase';
import type {
  SitePage,
  SiteBlock,
  SitePageWithBlocks,
  SitePageType,
  BlockType,
  UpdateSitePageInput,
  CreateBlockInput,
  UpdateBlockInput,
  BlockSettings,
} from '../types/site';

export interface PublicPageData extends SitePageWithBlocks {
  profile: Profile | null;
}

export async function getSitePages(userId: string): Promise<SitePage[]> {
  const { data, error } = await supabase
    .from('site_pages')
    .select('*')
    .eq('user_id', userId)
    .order('type');

  if (error) throw error;
  return data || [];
}

export async function getSitePageByType(
  userId: string,
  type: SitePageType
): Promise<SitePageWithBlocks | null> {
  const { data: page, error } = await supabase
    .from('site_pages')
    .select('*')
    .eq('user_id', userId)
    .eq('type', type)
    .maybeSingle();

  if (error) throw error;
  if (!page) return null;

  const { data: blocks, error: blocksError } = await supabase
    .from('site_blocks')
    .select('*')
    .eq('site_page_id', page.id)
    .order('block_order', { ascending: true });

  if (blocksError) throw blocksError;

  return { ...page, blocks: blocks || [] };
}

export async function getPublicPageBySlug(
  slug: string,
  type: SitePageType
): Promise<PublicPageData | null> {
  const { data: page, error } = await supabase
    .from('site_pages')
    .select('*')
    .eq('slug', slug)
    .eq('type', type)
    .eq('is_published', true)
    .maybeSingle();

  if (error) throw error;
  if (!page) return null;

  const { data: blocks, error: blocksError } = await supabase
    .from('site_blocks')
    .select('*')
    .eq('site_page_id', page.id)
    .order('block_order', { ascending: true });

  if (blocksError) throw blocksError;

  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', page.user_id)
    .maybeSingle();

  return { ...page, blocks: blocks || [], profile: profile || null };
}

export async function updateSitePage(
  pageId: string,
  userId: string,
  input: UpdateSitePageInput
): Promise<SitePage> {
  const { data, error } = await supabase
    .from('site_pages')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', pageId)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createBlock(
  userId: string,
  input: CreateBlockInput
): Promise<SiteBlock> {
  const { data: page } = await supabase
    .from('site_pages')
    .select('id')
    .eq('id', input.site_page_id)
    .eq('user_id', userId)
    .maybeSingle();

  if (!page) throw new Error('Page not found');

  const { data, error } = await supabase
    .from('site_blocks')
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateBlock(
  blockId: string,
  userId: string,
  input: UpdateBlockInput
): Promise<SiteBlock> {
  const { data: block } = await supabase
    .from('site_blocks')
    .select('site_page_id')
    .eq('id', blockId)
    .maybeSingle();

  if (!block) throw new Error('Block not found');

  const { data: page } = await supabase
    .from('site_pages')
    .select('id')
    .eq('id', block.site_page_id)
    .eq('user_id', userId)
    .maybeSingle();

  if (!page) throw new Error('Unauthorized');

  const { data, error } = await supabase
    .from('site_blocks')
    .update({
      ...input,
      updated_at: new Date().toISOString(),
    })
    .eq('id', blockId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteBlock(blockId: string, userId: string): Promise<void> {
  const { data: block } = await supabase
    .from('site_blocks')
    .select('site_page_id')
    .eq('id', blockId)
    .maybeSingle();

  if (!block) throw new Error('Block not found');

  const { data: page } = await supabase
    .from('site_pages')
    .select('id')
    .eq('id', block.site_page_id)
    .eq('user_id', userId)
    .maybeSingle();

  if (!page) throw new Error('Unauthorized');

  const { error } = await supabase
    .from('site_blocks')
    .delete()
    .eq('id', blockId);

  if (error) throw error;
}

export async function updateBlockOrders(
  userId: string,
  updates: { id: string; block_order: number }[]
): Promise<void> {
  for (const update of updates) {
    const { data: block } = await supabase
      .from('site_blocks')
      .select('site_page_id')
      .eq('id', update.id)
      .maybeSingle();

    if (!block) continue;

    const { data: page } = await supabase
      .from('site_pages')
      .select('id')
      .eq('id', block.site_page_id)
      .eq('user_id', userId)
      .maybeSingle();

    if (!page) continue;

    await supabase
      .from('site_blocks')
      .update({ block_order: update.block_order })
      .eq('id', update.id);
  }
}

// Placeholder image URLs - these will be replaced with actual Supabase URLs later
const PLACEHOLDER_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80',
  profile: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
  story: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600&q=80',
};

interface DefaultBlockConfig {
  type: BlockType;
  settings: BlockSettings;
  show_in_menu: boolean;
  menu_label: string | null;
  background?: {
    type: 'none' | 'color' | 'gradient' | 'image';
    color: string | null;
    gradient: string | null;
    imageUrl: string | null;
  };
}

function getDefaultBusinessBlocks(profile?: Profile | null, systemFormId?: string | null): DefaultBlockConfig[] {
  const displayName = profile?.full_name || 'Dein Name';
  const phoneNumber = profile?.phone?.replace(/[^0-9]/g, '') || '4917612345678';

  return [
    // 1) HERO - Erster Eindruck + klarer CTA
    {
      type: 'HERO',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'gradient',
        color: null,
        gradient: 'linear-gradient(135deg, #FDF7F2 0%, #E6F4F1 100%)',
        imageUrl: null,
      },
      settings: {
        title: 'Dein Start ins Forever Business – klar, persönlich und strukturiert',
        subtitle: 'Wenn du dir nebenbei (oder hauptberuflich) etwas Eigenes aufbauen willst, brauchst du kein perfektes Instagram und kein Verkaufstalent. Du brauchst einen Plan, klare Schritte und jemanden, der dich begleitet.',
        imageUrl: profile?.profile_image_url || PLACEHOLDER_IMAGES.hero,
        buttonLabel: 'Unverbindlich kennenlernen',
        buttonScrollToBlock: 'block_form',
        layout: 'image-right',
      },
    },
    // 2) PROBLEM_SOLUTION - Ich verstehe deinen Alltag
    {
      type: 'PROBLEM_SOLUTION',
      show_in_menu: true,
      menu_label: 'So geht\'s vielen',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Kommt dir das bekannt vor?',
        layout: 'left-right',
        problems: [
          { icon: 'X', text: 'Du willst starten, aber dir fehlt ein klarer Plan, was du wann tun sollst.' },
          { icon: 'X', text: 'Du hast Sorge, Freunde/Familie zu nerven – und schiebst das Ansprechen vor dir her.' },
          { icon: 'X', text: 'Du verlierst den Überblick: WhatsApp, Notizen, Kopf – alles verteilt.' },
          { icon: 'X', text: 'Du bist motiviert, aber dir fehlt eine Struktur, die in deinen Alltag passt.' },
        ],
        solutions: [
          { icon: 'Check', text: 'Ein einfacher Startplan mit klaren Schritten – ohne Überforderung.' },
          { icon: 'Check', text: 'Eine natürliche Ansprache, die zu dir passt – ohne Druck & ohne Peinlichkeit.' },
          { icon: 'Check', text: 'Ein System, das Leads und Follow-ups zuverlässig organisiert.' },
          { icon: 'Check', text: 'Routinen, die neben Job/Familie funktionieren – realistisch und umsetzbar.' },
        ],
      },
    },
    // 3) BENEFITS - Was du bei mir bekommst
    {
      type: 'BENEFITS',
      show_in_menu: true,
      menu_label: 'Deine Vorteile',
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Was du von mir erwarten kannst',
        items: [
          { icon: 'Compass', title: 'Klarer Fahrplan', text: 'Du weißt jederzeit, was der nächste sinnvolle Schritt ist – ohne Rätselraten.' },
          { icon: 'Handshake', title: 'Begleitung auf Augenhöhe', text: 'Du bist nicht allein – wir arbeiten gemeinsam an deinem Start und deiner Routine.' },
          { icon: 'FolderOpen', title: 'Struktur statt Chaos', text: 'Kontakte, Gespräche und Follow-ups werden sauber organisiert – damit nichts verloren geht.' },
          { icon: 'Calendar', title: 'Alltagstauglich', text: 'Neben Job, Kindern oder Alltag: Du bekommst ein System, das realistisch umsetzbar ist.' },
          { icon: 'ChatCircle', title: 'Sichere Kommunikation', text: 'Du bekommst Vorlagen & Leitfäden, damit du dich beim Schreiben & Sprechen sicher fühlst.' },
        ],
      },
    },
    // 4) STEPS - So läuft dein Einstieg ab
    {
      type: 'STEPS',
      show_in_menu: true,
      menu_label: 'So startest du',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'So läuft der Einstieg ab – transparent & ohne Druck',
        steps: [
          { number: 1, title: 'Kennenlernen', text: 'Wir sprechen 15–20 Minuten und klären: Passt das überhaupt zu dir und deinem Alltag?' },
          { number: 2, title: 'Dein Plan', text: 'Du bekommst einen einfachen Startplan (Kontakte, Routine, erste Schritte) – angepasst an deine Zeit.' },
          { number: 3, title: 'Erste Ergebnisse', text: 'Wir setzen gemeinsam die ersten Aktionen um – damit du Sicherheit bekommst und dranbleibst.' },
          { number: 4, title: 'Routine & Wachstum', text: 'Wenn du möchtest: Wir bauen Struktur auf, die du dauerhaft durchziehen kannst – Schritt für Schritt.' },
        ],
      },
    },
    // 5) ABOUT - Wer ich bin
    {
      type: 'ABOUT',
      show_in_menu: true,
      menu_label: 'Über mich',
      background: {
        type: 'color',
        color: '#E6F4F1',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: `Hi, ich bin ${displayName}`,
        text: profile?.short_bio || 'Ich arbeite mit Menschen, die sich etwas Eigenes aufbauen wollen – ohne lautes Marketing und ohne Druck. Mir ist wichtig, dass du dich wohlfühlst, einen klaren Plan hast und genau weißt, was zu tun ist. Wenn du strukturiert arbeiten möchtest und echte Begleitung suchst, bist du hier richtig.',
        imageUrl: profile?.profile_image_url || PLACEHOLDER_IMAGES.profile,
      },
    },
    // 6) STORY - Warum ich das mache
    {
      type: 'STORY',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Meine Geschichte in kurz',
        text: profile?.story || 'Ich kenne das Gefühl, etwas verändern zu wollen – aber nicht zu wissen, wie man es im Alltag unterbringt. Genau deshalb liebe ich ein System, das einfach ist und funktioniert: klare Schritte, echte Routinen und Fortschritt, den man spürt. Forever war für mich nicht „der schnelle Weg", sondern der strukturierte. Und genau so begleite ich auch andere.',
        imageUrl: PLACEHOLDER_IMAGES.story,
      },
    },
    // 7) TESTIMONIALS - Social Proof
    {
      type: 'TESTIMONIALS',
      show_in_menu: true,
      menu_label: 'Erfahrungen',
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Was andere sagen',
        testimonials: [
          { name: 'Nina', text: 'Ich hatte vorher null Struktur. Jetzt weiß ich jeden Tag, was ich tun soll – und ich bleibe dran.', imageUrl: '' },
          { name: 'Sabrina', text: 'Ohne Druck, ohne peinliche Nachrichten. Die klare Schritt-für-Schritt-Begleitung hat mir total geholfen.', imageUrl: '' },
          { name: 'Melanie', text: 'Endlich ein System, das neben Job und Familie funktioniert. Das war für mich der Gamechanger.', imageUrl: '' },
        ],
      },
    },
    // 8) FAQ - Einwände professionell entkräften
    {
      type: 'FAQ',
      show_in_menu: true,
      menu_label: 'FAQ',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Häufige Fragen',
        items: [
          { question: 'Muss ich dafür super extrovertiert sein?', answer: 'Nein. Es geht nicht um Lautsein, sondern um klare Schritte und eine natürliche Kommunikation, die zu dir passt.' },
          { question: 'Kann ich das nebenbei machen?', answer: 'Ja. Viele starten neben Job und Familie. Wichtig ist eine Routine, die realistisch ist – genau dabei helfe ich dir.' },
          { question: 'Muss ich Freunde und Familie ansprechen?', answer: 'Du entscheidest, wen du ansprichst. Wir finden gemeinsam eine saubere Strategie, ohne jemanden zu bedrängen.' },
          { question: 'Gibt es Druck oder Vorgaben?', answer: 'Nein. Du bestimmst Tempo und Umfang. Mein Fokus ist Struktur & Begleitung – nicht Druck.' },
          { question: 'Wie läuft das Kennenlernen ab?', answer: 'Ganz entspannt: kurze Nachricht oder Formular, dann ein kurzes Gespräch. Danach entscheidest du in Ruhe.' },
        ],
      },
    },
    // 9) CTA - Letzter Schub Richtung Kontakt
    {
      type: 'CTA',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#E7EAF2',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Wenn du möchtest: Lass uns kurz sprechen und schauen, ob es passt.',
        buttonLabel: 'Kennenlern-Gespräch anfragen',
        buttonScrollToBlock: 'block_form',
      },
    },
    // 10) FORM - Standard Kontaktformular
    {
      type: 'FORM',
      show_in_menu: true,
      menu_label: 'Kontakt',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Schreib mir – ich melde mich persönlich bei dir',
        formId: systemFormId || null,
      },
    },
    // 11) WHATSAPP_BUTTON - Low-friction Alternative
    {
      type: 'WHATSAPP_BUTTON',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        phoneNumber: phoneNumber,
        message: 'Hi 👋 Ich habe deine Business-Seite gesehen und würde gerne mehr erfahren. Hast du kurz Zeit für ein Kennenlernen?',
        buttonLabel: 'Per WhatsApp schreiben',
      },
    },
  ];
}

function getDefaultProductsBlocks(profile?: Profile | null, systemFormId?: string | null): DefaultBlockConfig[] {
  const phoneNumber = profile?.phone?.replace(/[^0-9]/g, '') || '4917612345678';

  return [
    // 1) HERO - Produktwelt + Beratung statt Shop
    {
      type: 'HERO',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'gradient',
        color: null,
        gradient: 'linear-gradient(135deg, #FDF7F2 0%, #E6F4F1 100%)',
        imageUrl: null,
      },
      settings: {
        title: 'Produkte, die in deinen Alltag passen – verständlich erklärt',
        subtitle: 'Hier findest du einen klaren Überblick, wofür welche Produkte gedacht sind, wie du sie anwendest und was sinnvoll zusammenpasst. Wenn du magst, berate ich dich persönlich – ohne Druck.',
        imageUrl: profile?.profile_image_url || PLACEHOLDER_IMAGES.hero,
        buttonLabel: 'Beratung anfragen',
        buttonScrollToBlock: 'block_form',
        layout: 'image-right',
      },
    },
    // 2) HIGHLIGHTS - Qualitäts- & Beratungsversprechen
    {
      type: 'HIGHLIGHTS',
      show_in_menu: true,
      menu_label: 'Warum diese Produkte',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Worauf du dich verlassen kannst',
        items: [
          { icon: 'Flask', title: 'Qualität & Transparenz', text: 'Du bekommst Orientierung, worauf es bei Inhaltsstoffen und Anwendung wirklich ankommt.' },
          { icon: 'Compass', title: 'Klare Empfehlungen', text: 'Kein Überangebot: Ich helfe dir, das Passende für dein Ziel zu finden.' },
          { icon: 'Heart', title: 'Beratung statt Verkauf', text: 'Du entscheidest in Ruhe. Ich bin da für Fragen, nicht für Druck.' },
          { icon: 'PushPin', title: 'Alltagstaugliche Routinen', text: 'Einfach integrierbar – mit klaren Schritten (morgens/abends/unterwegs).' },
        ],
      },
    },
    // 3) PRODUCT_SHOWCASE - Produkt-Übersicht (Grid)
    {
      type: 'PRODUCT_SHOWCASE',
      show_in_menu: true,
      menu_label: 'Produktübersicht',
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Beliebte Produkte & Kategorien im Überblick',
        subtitle: 'Klicke dich durch – oder sag mir kurz, was dein Ziel ist (Haut, Energie, Wohlbefinden, Routine). Dann empfehle ich dir die passenden Produkte.',
        columns: 3,
        selectedProducts: [], // User wählt später Produkte aus
        products: [], // Legacy format, leer
      },
    },
    // 4) IMAGE_TEXT - Routine statt Einzelkauf
    {
      type: 'IMAGE_TEXT',
      show_in_menu: true,
      menu_label: 'Anwendung & Routine',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'So entsteht eine gute Routine (ohne Überforderung)',
        text: 'Die besten Ergebnisse entstehen nicht durch „mehr Produkte", sondern durch eine einfache Routine, die du wirklich durchziehst. Ich helfe dir, 2–4 Produkte sinnvoll zu kombinieren – passend zu deinem Ziel und deinem Alltag.',
        imageUrl: PLACEHOLDER_IMAGES.story,
        layout: 'image-left',
      },
    },
    // 5) ADVANCED_VIDEO - optionales Erklärvideo
    {
      type: 'ADVANCED_VIDEO',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#E6F4F1',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Kurz erklärt: Wie ich dich berate (in 60 Sekunden)',
        videoType: 'youtube',
        youtubeUrl: '',
        videoUrl: null,
        autoplay: false,
        rounded: true,
      },
    },
    // 6) TESTIMONIALS - Kundenerfahrungen
    {
      type: 'TESTIMONIALS',
      show_in_menu: true,
      menu_label: 'Erfahrungen',
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Erfahrungen aus dem Alltag',
        testimonials: [
          { name: 'Julia', text: 'Endlich habe ich verstanden, was wofür ist – und wie ich es wirklich anwende. Die Routine ist super einfach.', imageUrl: '' },
          { name: 'Katharina', text: 'Ich hatte vorher tausend Fragen. Die Beratung war entspannt und ohne Druck – genau so muss das sein.', imageUrl: '' },
          { name: 'Mona', text: 'Die Produktauswahl war viel klarer, als ich dachte. Mit 3 Produkten habe ich jetzt eine saubere Routine.', imageUrl: '' },
        ],
      },
    },
    // 7) FAQ - Kaufbarrieren abbauen
    {
      type: 'FAQ',
      show_in_menu: true,
      menu_label: 'FAQ',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Häufige Fragen zu Produkten & Beratung',
        items: [
          { question: 'Muss ich gleich mehrere Produkte kaufen?', answer: 'Nein. Oft reichen 1–3 Produkte, wenn sie sinnvoll gewählt sind. Ich helfe dir bei einer klaren, kleinen Auswahl.' },
          { question: 'Wie finde ich das passende Produkt für mich?', answer: 'Sag mir kurz dein Ziel (z.B. Haut, Wohlbefinden, Energie) und was in deinem Alltag realistisch ist. Dann empfehle ich dir eine passende Routine.' },
          { question: 'Kann ich Fragen stellen, bevor ich etwas bestelle?', answer: 'Unbedingt. Schreib mir einfach – ich berate dich persönlich und transparent.' },
          { question: 'Sind die Produkte für jeden geeignet?', answer: 'Jeder Körper ist individuell. Wenn du Unverträglichkeiten hast oder unsicher bist, schauen wir gemeinsam, was sinnvoll ist.' },
          { question: 'Ist das hier ein Shop?', answer: 'Nein – es ist eine Beratungsseite. Du bekommst Orientierung und Unterstützung, damit du sicher entscheiden kannst.' },
        ],
      },
    },
    // 8) CTA - nochmal klar zur Beratung führen
    {
      type: 'CTA',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#E7EAF2',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Sag mir kurz dein Ziel – ich stelle dir eine einfache Produktempfehlung zusammen.',
        buttonLabel: 'Beratung anfragen',
        buttonScrollToBlock: 'block_form',
      },
    },
    // 9) FORM - Anfrage/Lead
    {
      type: 'FORM',
      show_in_menu: true,
      menu_label: 'Kontakt',
      background: {
        type: 'color',
        color: '#FFFFFF',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        title: 'Schreib mir kurz – ich empfehle dir die passenden Produkte',
        formId: systemFormId || null,
      },
    },
    // 10) WHATSAPP_BUTTON - Low-friction Kontakt
    {
      type: 'WHATSAPP_BUTTON',
      show_in_menu: false,
      menu_label: null,
      background: {
        type: 'color',
        color: '#F5F5F5',
        gradient: null,
        imageUrl: null,
      },
      settings: {
        phoneNumber: phoneNumber,
        message: 'Hi 👋 Ich war auf deiner Produktseite und hätte gern eine Empfehlung. Mein Ziel ist: ...',
        buttonLabel: 'Per WhatsApp fragen',
      },
    },
  ];
}

// Helper function to get the system form ID for a user
async function getSystemFormId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('forms')
    .select('id')
    .eq('user_id', userId)
    .eq('is_system', true)
    .maybeSingle();

  return data?.id || null;
}

// Default page settings
const DEFAULT_BUSINESS_PAGE_SETTINGS = {
  title: 'Dein Start mit Forever – strukturiert, persönlich, ohne Druck',
  description: 'Erfahre, wie du dir nebenbei ein strukturiertes Business aufbauen kannst – mit klaren Schritten, persönlicher Begleitung und Fokus auf echte Ergebnisse.',
  theme_primary_color: '#1F2A37',
  theme_accent_color: '#E6F4F1',
};

const DEFAULT_PRODUCTS_PAGE_SETTINGS = {
  title: 'Produkte – Beratung, Anwendung & Empfehlungen',
  description: 'Entdecke die Produktwelt – verständlich erklärt. Ich helfe dir, die passenden Produkte für deinen Alltag zu finden – ohne Druck, mit persönlicher Beratung.',
  theme_primary_color: '#1F2A37',
  theme_accent_color: '#E6F4F1',
};

export async function ensureUserPages(userId: string, userSlug: string): Promise<void> {
  const { data: existingPages } = await supabase
    .from('site_pages')
    .select('type')
    .eq('user_id', userId);

  const existingTypes = existingPages?.map((p) => p.type) || [];

  if (existingTypes.includes('BUSINESS') && existingTypes.includes('PRODUCTS')) {
    return;
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  const systemFormId = await getSystemFormId(userId);

  if (!existingTypes.includes('BUSINESS')) {
    const { data: businessPage } = await supabase
      .from('site_pages')
      .insert({
        user_id: userId,
        type: 'BUSINESS',
        slug: userSlug,
        ...DEFAULT_BUSINESS_PAGE_SETTINGS,
      })
      .select()
      .single();

    if (businessPage) {
      const defaultBlocks = getDefaultBusinessBlocks(profile, systemFormId);
      const blocks = defaultBlocks.map((block, index) => ({
        site_page_id: businessPage.id,
        type: block.type,
        block_order: index,
        visible: true,
        show_in_menu: block.show_in_menu,
        menu_label: block.menu_label,
        settings: {
          ...block.settings,
          background: block.background,
        },
      }));

      await supabase.from('site_blocks').insert(blocks);
    }
  }

  if (!existingTypes.includes('PRODUCTS')) {
    const { data: productsPage } = await supabase
      .from('site_pages')
      .insert({
        user_id: userId,
        type: 'PRODUCTS',
        slug: userSlug,
        ...DEFAULT_PRODUCTS_PAGE_SETTINGS,
      })
      .select()
      .single();

    if (productsPage) {
      const defaultBlocks = getDefaultProductsBlocks(profile, systemFormId);
      const blocks = defaultBlocks.map((block, index) => ({
        site_page_id: productsPage.id,
        type: block.type,
        block_order: index,
        visible: true,
        show_in_menu: block.show_in_menu,
        menu_label: block.menu_label,
        settings: {
          ...block.settings,
          background: block.background,
        },
      }));

      await supabase.from('site_blocks').insert(blocks);
    }
  }
}

/**
 * Resets a page to its default template.
 * Deletes all existing blocks and recreates them from the default template.
 * Also resets the page title, description, and colors.
 */
export async function resetPageToDefault(
  pageId: string,
  userId: string,
  pageType: SitePageType
): Promise<void> {
  // Verify page ownership
  const { data: page } = await supabase
    .from('site_pages')
    .select('id, slug')
    .eq('id', pageId)
    .eq('user_id', userId)
    .single();

  if (!page) throw new Error('Page not found');

  // Get user profile and system form
  const { data: profile } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  const systemFormId = await getSystemFormId(userId);

  // Delete all existing blocks
  await supabase
    .from('site_blocks')
    .delete()
    .eq('site_page_id', pageId);

  // Get default settings based on page type
  const pageSettings = pageType === 'BUSINESS'
    ? DEFAULT_BUSINESS_PAGE_SETTINGS
    : DEFAULT_PRODUCTS_PAGE_SETTINGS;

  // Update page settings
  await supabase
    .from('site_pages')
    .update({
      ...pageSettings,
      updated_at: new Date().toISOString(),
    })
    .eq('id', pageId);

  // Get default blocks based on page type
  const defaultBlocks = pageType === 'BUSINESS'
    ? getDefaultBusinessBlocks(profile, systemFormId)
    : getDefaultProductsBlocks(profile, systemFormId);

  // Create new blocks
  const blocks = defaultBlocks.map((block, index) => ({
    site_page_id: pageId,
    type: block.type,
    block_order: index,
    visible: true,
    show_in_menu: block.show_in_menu,
    menu_label: block.menu_label,
    settings: {
      ...block.settings,
      ...(block.background ? { background: block.background } : {}),
    },
  }));

  await supabase.from('site_blocks').insert(blocks);
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/[ß]/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
